// src/modules/finance/pages/components/context/AccountingContext.jsx
import { createContext, useContext, useMemo, useReducer } from "react";
import {
  normalizeEntry,
  computeLedger,
  computeTrialBalance,
  computeIncomeStatement,
  computeBalanceSheet,
} from "../../../lib/accounting.js"; // single import
import { TEMPLATE_ENTRIES, SAMPLE_JOURNAL } from "../../../data/placeholder.js";

const AccountingContext = createContext(null);

/* Build full derived finance state from a journal array */
function buildState(journalRows, ledgerWorkflow = null) {
  // Normalize entries - keep empty array if explicitly set to empty
  const source = Array.isArray(journalRows) 
    ? journalRows.map(normalizeEntry)
    : []; // HIDDEN: was TEMPLATE_ENTRIES.map(normalizeEntry)

  const ledger = computeLedger(source);
  const tb = computeTrialBalance(ledger);
  const income = computeIncomeStatement(tb);
  const balance = computeBalanceSheet(tb, income);

  return {
    journal: source,
    ledger,
    trialBalance: tb,
    income,
    balance,
    cashflow: { operating: income.net, investing: 0, financing: 0, netChange: income.net },
    ledgerWorkflow: ledgerWorkflow || {
      hasUnreviewedChanges: false,
      source: null,
      lastChangeAt: null,
      approvedAt: null,
    },
  };
}

/* Reducer */
function reducer(state, action) {
  switch (action.type) {
    case "SET_JOURNAL": {
      const payload = action.payload;
      const next = Array.isArray(payload) ? payload.map(normalizeEntry) : [];
      return buildState(next, state.ledgerWorkflow);
    }
    case "ADD_ENTRY": {
      const next = [...state.journal, normalizeEntry(action.payload)];
      return buildState(next, state.ledgerWorkflow);
    }
    case "ADD_BULK_ENTRIES": {
      const bulkEntries = (action.payload || []).map(normalizeEntry);
      const next = [...state.journal, ...bulkEntries];
      // Mark as unreviewed when bulk importing
      const workflow = {
        hasUnreviewedChanges: true,
        source: action.meta?.source || "import",
        lastChangeAt: Date.now(),
        approvedAt: null,
      };
      return buildState(next, workflow);
    }
    case "UPDATE_ENTRY": {
      const { id, entry } = action.payload;
      const next = state.journal.map((e) =>
        (e.id === id || e.entryId === id) ? { ...normalizeEntry(entry), id } : e
      );
      return buildState(next, state.ledgerWorkflow);
    }
    case "APPROVE_LEDGER": {
      const workflow = {
        ...state.ledgerWorkflow,
        hasUnreviewedChanges: false,
        approvedAt: Date.now(),
      };
      return { ...state, ledgerWorkflow: workflow };
    }
    default:
      return state;
  }
}

/* Provider + hook */
/* Start with empty journal by default */
export function AccountingProvider({ initialJournal = [], children }) {
  const [state, dispatch] = useReducer(reducer, buildState(initialJournal ?? []));

  const api = useMemo(
    () => ({
      state,
      setJournal: (rows) => dispatch({ type: "SET_JOURNAL", payload: rows }),
      addEntry:  (entry) => dispatch({ type: "ADD_ENTRY",  payload: entry }),
      addBulkJournalEntries: (entries, meta) => dispatch({ type: "ADD_BULK_ENTRIES", payload: entries, meta }),
      updateEntry: (id, entry) => dispatch({ type: "UPDATE_ENTRY", payload: { id, entry } }),
      approveLedger: () => dispatch({ type: "APPROVE_LEDGER" }),
    }),
    [state]
  );

  return (
    <AccountingContext.Provider value={api}>
      {children}
    </AccountingContext.Provider>
  );
}

export function useAccounting() {
  const ctx = useContext(AccountingContext);
  if (!ctx) throw new Error("useAccounting must be used inside <AccountingProvider>");
  return ctx;
}
